import React, { useEffect, useMemo, useState } from 'react';
import { useAppSelector } from '@store/hooks';
import {
  selectUsersDataFilterAuthPerson,
  selectUsersDataFilterVerifiedAuthPerson,
  selectUsersDataFilterDirector,
  selectUsersDataFilterVerifiedDirector,
  selectDocumentsFilterBusiness,
  selectLatestProductsStatusInfo,
} from '@features/workspace/store/selectors';
import Box from '@mui/material/Box';
import { Link } from '@mui/material';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { makeStyles } from 'tss-react/mui';
import { Documents } from '@features/workspace/components/document-list';
import { CompanySummary } from '@features/workspace/components/company-summary';
import { PrimaryUser } from '@features/workspace/components/primary-user';
import { QuickLinks } from '@features/workspace/components/quick-links';
import { LatestProductStart } from '@features/workspace/components/shared/latest-product-start';
import { LatestProductStatus } from '@features/workspace/components/shared/latest-product-status';
import LandingLayout from '@features/workspace/views/landing/components/layout';
import { ProductInfoStatus } from '@features/workspace/views/landing/components/product-info-status';
import { VerifiedCounter } from '@features/workspace/views/landing/components/verified-counter';
import {
  applicationStatusActiveList,
  ProductTypeEnum,
  getBankAccountStatus,
  getApplicationStatusText,
} from '@utils/constants';
import { getPageById } from '@app/services/pages-meta';

const useStyles = makeStyles<any>()({
  highlightedContainer: {
    backgroundColor: '#F5F6FA',
  },

  divider: {
    marginBottom: '25px !important',
  },
});

const LandingPage: React.FC = () => {
  const { classes } = useStyles(null);

  useEffect(() => {
    document.title = 'Lula Support - Customer Overview';
    }, []) // eslint-disable-line

  const [hasFundingProduct, setHasFundingProduct] = useState(false);

  const authPersons = useAppSelector(selectUsersDataFilterAuthPerson);
  const verifiedAuthPersons = useAppSelector(
    selectUsersDataFilterVerifiedAuthPerson
  );
  const directors = useAppSelector(selectUsersDataFilterDirector);
  const verifiedDirectors = useAppSelector(
    selectUsersDataFilterVerifiedDirector
  );
  const businessDocuments = useAppSelector(selectDocumentsFilterBusiness);
  const latestProductsInfo = useAppSelector(selectLatestProductsStatusInfo);

  const latestFundingApplication: any = useMemo(() => {
    const fundingApplication = latestProductsInfo.find(
      (p) => p.latestProduct.type === ProductTypeEnum.Funding
    )?.latestProduct?.item;

    setHasFundingProduct(fundingApplication);

    return fundingApplication;
  }, [latestProductsInfo]);

  const creditProductIsCompleted: boolean | null = useMemo(
    () => latestFundingApplication?.status in applicationStatusActiveList,
    [latestFundingApplication]
  );

  const creditProductStartDate: string = useMemo(
    () => latestFundingApplication?.createdDate || '',
    [latestFundingApplication]
  );

  const creditStatusMessage: string | null = useMemo(
    () =>
      latestFundingApplication?.status
        ? getApplicationStatusText(latestFundingApplication)
        : null,
    [latestFundingApplication]
  );

  const lulaBankAccounts: any = useMemo(() => {
    const bankAccounts = [];
    const bankAccount = latestProductsInfo.find(
      (p: any) => p.latestProduct.type === ProductTypeEnum.Banking
    )?.latestProduct?.item;

    if (bankAccount) {
      bankAccounts.push(bankAccount);
    }

    return bankAccounts;
  }, [latestProductsInfo]);

  // const bankingProductIsCompleted: boolean | null = useMemo(
  //     () => hasBankingProduct ? !!(lulaBankAccounts?.[0]?.statusCode === "00") : null,
  //     [lulaBankAccounts]
  // )

  const bankingProductStartDate: string = useMemo(
    () => lulaBankAccounts?.[0]?.createdAt || '',
    [lulaBankAccounts]
  );

  const hasBankingProduct = useMemo(
    () => !!lulaBankAccounts?.length,
    [lulaBankAccounts]
  );

  const bankingStatusMessage: string | null = useMemo(() => {
    if (hasBankingProduct) {
      return (
        getBankAccountStatus(lulaBankAccounts?.[0]?.statusCode) ?? 'Pending'
      );
    }

    return null;
  }, [lulaBankAccounts, hasBankingProduct]);

  const hasPendingBankingProduct = useMemo(
    () => bankingStatusMessage && bankingStatusMessage === 'Pending',
    [bankingStatusMessage]
  );

  return (
    <>
      <LandingLayout>
        {latestProductsInfo && latestProductsInfo.length > 0 && (
          <>
            <Box px={2.5} py={2}>
              <Grid container>
                <Grid item xs={8}>
                  <LatestProductStart productInfo={latestProductsInfo[0]} />
                </Grid>
                <Grid item xs={4}>
                  <LatestProductStatus productInfo={latestProductsInfo[0]} />
                </Grid>
              </Grid>
            </Box>

            <Divider />
          </>
        )}

        {/* BUSINESS DETAILS */}
        <Box px={2.5} py={2}>
          <Typography variant="h6">Business details</Typography>
          <Box display="flex" pt={1.5}>
            <CompanySummary />
          </Box>
        </Box>

        <Divider />

        {/* DOCUMENTS */}
        <Box px={2.5} py={2}>
          {(hasBankingProduct || hasPendingBankingProduct) && (
            <>
              <Box display="flex" flexDirection="row">
                <Box display="flex">
                  <Typography variant="h6">
                    <Link
                      href={getPageById('documents')?.uri}
                      target="LulaCustomerDetails"
                    >
                      Business documents
                    </Link>
                  </Typography>
                </Box>
                <Box display="flex" ml={1}>
                  <Typography
                    className="labelHeader"
                    sx={{ marginTop: '1px' }}
                    variant="subtitle2"
                  >
                    (Bank)
                  </Typography>
                </Box>
              </Box>
              <Box display="flex" pt={1.5}>
                <Documents
                  showModifiedDate
                  items={businessDocuments}
                  showVerifiedLabel={false}
                />
              </Box>
            </>
          )}

          {hasFundingProduct && (
            <>
              {/* TODO: Not displaying this for now until can determine where to fetch data from (not documents) */}
              {/* <Box 
                                mt={2}
                                ml={0}
                                display={'flex'}
                                flexDirection={'row'}
                            >
                                <Box sx={{paddingRight:'10px'}}>
                                    <Typography 
                                        variant='h6'
                                    >
                                        <Link 
                                            href={getPageById('documents')?.uri}
                                            target={'LulaCustomerDetails'}
                                        >
                                            Transactional data
                                        </Link>
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography 
                                        variant='subtitle2'
                                        className='labelHeader'
                                    >
                                        (Credit)
                                    </Typography>
                                </Box>
                            </Box>
                            <Documents 
                                items={[]} //TODO: transactional documents come from where? or are categorised by?
                                showVerifiedLabel={false}
                                showModifiedDate={true} 
                            /> */}
            </>
          )}
        </Box>

        {/* PRIMARY USER */}
        <Box className={classes.highlightedContainer} px={2.5} py={2}>
          <Typography variant="h6">Primary user</Typography>
          <Box display="flex" pt={1.25}>
            <PrimaryUser />
          </Box>
        </Box>
        {/* DIRECTORS / AUTH PERSONS */}
        <Box px={2.5} py={2}>
          <Grid container>
            <Grid item xs={6}>
              <Typography variant="h6">Directors</Typography>
              <Box mt={1}>
                <VerifiedCounter
                  totalCount={directors?.length}
                  verifiedCount={verifiedDirectors?.length}
                />
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="h6">Authorised person</Typography>
              <Box mt={1}>
                <VerifiedCounter
                  totalCount={authPersons?.length}
                  verifiedCount={verifiedAuthPersons.length}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
        {/* PRODUCTS */}
        <Box className={classes.highlightedContainer} px={2.5} py={2}>
          <Typography variant="h6">Products</Typography>
          <Grid container mt={1} spacing={0.5}>
            <Grid item xs={6}>
              <ProductInfoStatus
                activeStatus={creditProductIsCompleted}
                header="Lula Credit"
                headerLink={`${getPageById('credit')?.uri}`}
                productApplicationDate={creditProductStartDate}
                statusMessage={creditStatusMessage}
              />
            </Grid>
            <Grid item xs={6}>
              <ProductInfoStatus
                activeStatus={bankingStatusMessage === 'Active'}
                header="Lula Bank"
                headerLink={`${getPageById('banking')?.uri}`}
                productApplicationDate={bankingProductStartDate}
                statusMessage={bankingStatusMessage}
              />
            </Grid>
          </Grid>
        </Box>
        {/* QUICK LINKS */}
        {hasBankingProduct && (
          <Box className={classes.highlightedContainer}>
            <Box
              px={2.5}
              py={2}
              style={{
                borderTopLeftRadius: '20px',
                borderTopRightRadius: '20px',
                background: '#fff',
              }}
            >
              <QuickLinks />
            </Box>
          </Box>
        )}
      </LandingLayout>
    </>
  );
};

export default LandingPage;
